import {
  FilterX,
  Inbox,
  Lock,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";
import { useContext, type ReactNode } from "react";

import { FilterProblemsContext } from "@/components/filters/problems";
import {
  ListStateContext,
  nothingYetTitle,
} from "@/components/shell/list-state";
import { Button } from "@/components/ui/button";

interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
}

/**
 * What a page shows instead of rows.
 *
 * Deliberately three separate exports below rather than one component with a
 * `variant` prop: "nothing here yet", "nothing matches your filter" and
 * "this failed" are three different situations with three different next
 * actions, and collapsing them produces the screen that says "No data" and
 * leaves the user to work out which one it was.
 */
export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
}: EmptyStateProps) {
  return (
    <div className="flex h-full min-h-64 flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <Icon className="text-muted-foreground/60 size-8" aria-hidden />
      <div className="space-y-1">
        <p className="text-sm font-medium">{title}</p>
        {description ? (
          <p className="text-muted-foreground max-w-sm text-sm">
            {description}
          </p>
        ) : null}
      </div>
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}

/**
 * Nothing exists yet.
 *
 * The action is how you make the first one — or, for a mirrored list, nothing,
 * because you do not create an order here, Shopify does.
 */
export function NothingYet({
  title,
  description,
  action,
}: Omit<EmptyStateProps, "icon">) {
  return (
    <EmptyState
      icon={Inbox}
      title={title}
      description={description}
      action={action}
    />
  );
}

/**
 * Rows exist; the filter excludes all of them.
 *
 * Always offers a way back. A filtered-empty screen with no escape is how
 * someone concludes their data is gone.
 *
 * Says why when the filter itself is the reason (#518): a clause that can never
 * match, such as a date range that runs backwards, is named after the usual
 * sentence, from the problems `SectionPage` found in the filter.
 *
 * Inside a section where the person has narrowed nothing, the list is not
 * filtered-empty but simply empty, so it says "No {things} yet" instead, with
 * the section's create button when it has one (#642). Every list that draws
 * this gets the right wording without knowing which case it is in.
 */
export function NoMatches({
  onClear,
  description = "No rows match the current filter.",
  createAction,
}: {
  onClear: () => void;
  description?: string;
  /** How the first one is made, shown only in the "nothing yet" form. */
  createAction?: ReactNode;
}) {
  const problems = useContext(FilterProblemsContext);
  const list = useContext(ListStateContext);
  const reason = problems.length > 0 ? ` ${problems.join(". ")}.` : "";

  if (list && !list.narrowed) {
    return (
      <NothingYet
        title={nothingYetTitle(list)}
        description={list.nothingYet?.description}
        action={createAction}
      />
    );
  }

  return (
    <EmptyState
      icon={FilterX}
      title="Nothing matches"
      description={`${description}${reason}`}
      action={
        <Button size="sm" variant="outline" onClick={onClear}>
          Clear filters
        </Button>
      }
    />
  );
}

/**
 * The request failed.
 *
 * `message` is the server's own words where there are any. A generic
 * "Something went wrong" is unactionable, and the specific text is what makes
 * a support conversation take one message instead of five.
 */
export function LoadFailed({
  message,
  onRetry,
}: {
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <EmptyState
      icon={TriangleAlert}
      title="Could not load this"
      description={message ?? "The request did not complete."}
      action={
        onRetry ? (
          <Button size="sm" variant="outline" onClick={onRetry}>
            Try again
          </Button>
        ) : undefined
      }
    />
  );
}

/**
 * A section the signed-in persona holds no permission for.
 *
 * Says so, rather than redirecting to the dashboard. A silent bounce is
 * indistinguishable from a broken link: the person following it cannot tell
 * whether they mistyped the URL, the page moved, or they simply lack access —
 * and the one who sent it never finds out either.
 *
 * This is a courtesy, not enforcement. Anyone can edit the bundle. The real
 * check is `requirePermission()` on every route handler (FR-ACL-8, NFR-14),
 * and it runs regardless of what this component does.
 */
export function NoAccess({ permission }: { permission: string }) {
  return (
    <EmptyState
      icon={Lock}
      title="Not available to you"
      description={`This page needs the ${permission} permission, which your role does not hold. Ask an administrator if you need it.`}
    />
  );
}
